import { config } from '../../config';
import { NetworkActions } from '../network';
import { NetworkService } from '../../services';
import { ThermostatConstants } from './types';
import {
  connectionStateValues,
  demandValues,
  fanSettingValues,
  modeValues,
  stateValues,
  buildingSectionValues,
  directionValues,
} from '../../components/ThermostatStatus/thermostatFilterConstants';

// API endpoints
const { BASE_URL, THERMOSTAT_THERMOSTAT_URL, THERMOSTAT_SETUP_URL } = config;
const THERMOSTATS = `${BASE_URL}${THERMOSTAT_THERMOSTAT_URL}`;
const SETUP = `${BASE_URL}${THERMOSTAT_SETUP_URL}`;

function isFiltering(obj, keys) {
  return keys.some((key) => obj[key] === true);
}

export class ThermostatActions {
  static register(slug, floor, suite, direction) {
    return async (dispatch) => {
      dispatch({ type: ThermostatConstants.RequestNewThermostat });
      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          building_slug: slug,
          direction,
          unit_number: suite,
          floor_number: floor,
        }),
      );

      const response = await fetch(`${SETUP}register/`, requestOptions).catch(() => 'error');

      if (!response || !response.ok || response === 'error') {
        dispatch(ThermostatActions.stopRegistering());
        return dispatch({
          type: ThermostatConstants.ThermostatError,
          payload: 'There was an error registering the thermostat. Please try again.',
        });
      }

      const data = await response.json();
      return dispatch({ type: ThermostatConstants.ReceiveNewThermostat, payload: data });
    };
  }

  static refreshPin(uidHash) {
    return async (dispatch) => {
      dispatch({ type: ThermostatConstants.RequestNewPin });

      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          uid_hash: uidHash,
        }),
      );

      const response = await fetch(`${SETUP}${uidHash}/generate-pin/`, requestOptions);
      const data = await response.json();

      if (!response || !response.ok || response === 'error') {
        return dispatch({
          type: ThermostatConstants.ThermostatError,
          payload: 'There was an error refreshing the pin for the current thermostat.',
        });
      }

      return dispatch({ type: ThermostatConstants.ReceiveNewPin, payload: data.pin });
    };
  }

  static clearRefreshedPin() {
    return { type: ThermostatConstants.ClearRefreshedPin };
  }

  static pushSchedule(uidHash) {
    return async (dispatch, getState) => {
      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          uid_hash: uidHash,
        }),
      );

      const response = await fetch(`${SETUP}${uidHash}/push-schedule/`, requestOptions);
      const data = await response.json();
      const newState = getState();
      const { error } = newState.thermostatReducer;

      if (!response || !response.ok || response === 'error') {
        if (!error) {
          const { message } = data.errors[0];
          return dispatch({
            type: ThermostatConstants.ThermostatError,
            payload: message,
          });
        }
      }

      if (!error) {
        return dispatch({
          type: ThermostatConstants.SetRestoreMessage,
          payload: 'Thermostat restored successfully.',
        });
      }
    };
  }

  static pushSettings(uidHash) {
    return async (dispatch, getState) => {
      const requestOptions = NetworkService.getRequestOptions(
        'POST',
        JSON.stringify({
          uid_hash: uidHash,
        }),
      );

      const response = await fetch(`${SETUP}${uidHash}/push-settings/`, requestOptions);
      const data = await response.json();
      const newState = getState();
      const { error } = newState.thermostatReducer;

      if (!response || !response.ok || response === 'error') {
        if (!error) {
          const { message } = data.errors[0];
          return dispatch({
            type: ThermostatConstants.ThermostatError,
            payload: message,
          });
        }
      }

      if (!error) {
        return dispatch({
          type: ThermostatConstants.SetRestoreMessage,
          payload: 'Thermostat restored successfully.',
        });
      }
    };
  }

  static clearRestoreResults() {
    return { type: ThermostatConstants.ClearRestoreResults };
  }

  static pollThermostat(uidHash) {
    return async (dispatch) => {
      const requestOptions = NetworkService.getRequestOptions('GET');
      const urlFormatted = `${SETUP}${uidHash}/poll/`;
      const response = await fetch(urlFormatted, requestOptions).catch(() => 'error');

      if (!response || !response.ok || response === 'error') {
        let errorMessage = 'Polling Error';
        if (response && response !== 'error') {
          const data = await response.json();
          const { errors = [] } = data;
          if (errors.length > 0) {
            errorMessage = errors[0].message;
          }
        }
        dispatch({
          type: ThermostatConstants.ThermostatError,
          payload: errorMessage,
        });
        return dispatch({ type: ThermostatConstants.PollThermostat, payload: { success: false, should_poll: false } });
      }

      const data = await response.json();
      return dispatch({ type: ThermostatConstants.PollThermostat, payload: data });
    };
  }

  static setThermostats(slug, subcategory = undefined) {
    return async (dispatch) => {
      dispatch({ type: ThermostatConstants.RequestThermostats });
      const requestInfo = {
        category: 'thermostats',
        subcategory,
      };
      dispatch(NetworkActions.updateLastRequest(requestInfo));

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');

        const urlFormatted = `${THERMOSTATS}${slug}/`;
        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response || !response.ok || response === 'error') {
        return dispatch({
          type: ThermostatConstants.ThermostatError,
          payload: 'There was an error receiving thermostat data.',
        });
      }

      const data = await response.json();
      await dispatch({ type: ThermostatConstants.ReceiveThermostats, payload: data });
      return dispatch(ThermostatActions.updateFilters({}));
    };
  }

  static updateFilters(filters) {
    return async (dispatch, getState) => {
      const { thermostatFilters } = getState().thermostatReducer;
      const newFilters = {
        ...thermostatFilters,
        ...filters,
      };
      await dispatch({ type: ThermostatConstants.UpdateFilters, payload: newFilters });
      return dispatch(ThermostatActions.updateFilteredThermostats(newFilters));
    };
  }

  static clearFilters() {
    return async (dispatch) => {
      const clearedFilters = {
        connection_state: {},
        demand: {},
        direction: {},
        fan_mode: {},
        building_section: {},
        mode: {},
        state: {},
        searchValue: '',
      };
      await dispatch(ThermostatActions.updateFilteredThermostats(clearedFilters));
      return dispatch({ type: ThermostatConstants.UpdateFilters, payload: clearedFilters });
    };
  }

  static updateFilteredThermostats(filters) {
    return async (dispatch, getState) => {
      const { allThermostats } = getState().thermostatReducer;
      let filteredThermostats = [...allThermostats];

      const {
        connection_state: connectionState = {},
        demand = {},
        direction = {},
        fan_mode: fanMode = {},
        building_section: buildingSection = {},
        mode = {},
        state = {},
        searchValue = '',
      } = filters;

      if (isFiltering(connectionState, connectionStateValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'connection_state',
          connectionStateValues,
          filteredThermostats,
        );
      }

      if (isFiltering(mode, modeValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'mode',
          modeValues,
          filteredThermostats,
        );
      }

      if (isFiltering(fanMode, fanSettingValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'fan_mode',
          fanSettingValues,
          filteredThermostats,
        );
      }

      if (isFiltering(buildingSection, buildingSectionValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'building_section',
          buildingSectionValues,
          filteredThermostats,
        );
      }

      if (isFiltering(direction, directionValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'direction',
          directionValues,
          filteredThermostats,
        );
      }

      if (isFiltering(demand, demandValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'demand',
          demandValues,
          filteredThermostats,
        );
      }

      if (isFiltering(state, stateValues)) {
        filteredThermostats = ThermostatActions.filterThermostatsByKeys(
          filters,
          'state',
          stateValues,
          filteredThermostats,
        );
      }

      if (searchValue !== '') {
        filteredThermostats = ThermostatActions.searchThermostatList(
          searchValue,
          ['display_name'],
          filteredThermostats,
        );
      }

      const sortedThermostats = ThermostatActions.sortThermostats(filteredThermostats);
      const groupKeys = Object.keys(filters);

      // exclude searchValue from number of filters & count all other true values
      const numberOfFilters = groupKeys.reduce((accumulator, groupKey) => {
        if (groupKey === 'searchValue') {
          return accumulator + 0;
        }
        const keys = Object.keys(filters[groupKey]);
        const activeFilters = keys.filter((key) => filters[groupKey][key]);
        return accumulator + activeFilters.length;
      }, 0);

      dispatch(ThermostatActions.updateNumberOfFilters(numberOfFilters));
      dispatch(ThermostatActions.updateFilterCounts(sortedThermostats));
      return dispatch({ type: ThermostatConstants.UpdateFilteredThermostats, payload: sortedThermostats });
    };
  }

  static searchThermostatList(searchValue, keys, data) {
    let tempList = [];
    tempList = data.filter((item) =>
      keys.some((key) =>
        item[key]
          .replace(/\s/g, '')
          .toLowerCase()
          .includes(`${searchValue.replace(/\s/g, '').toLowerCase()}`),
      ),
    );
    return tempList;
  }

  static sortThermostats(list) {
    // sort by id
    const sortedList = list.sort((a, b) => a.id - b.id);
    return sortedList;
  }

  static filterThermostatsByKeys(options = {}, key = '', values = [], thermostats) {
    // check if value exists in options
    return values.reduce((accumulator, value) => {
      const optionExists = options[key][value] !== undefined;

      // return if the key doesn't exist or the option is set to false
      if (!optionExists || !options[key][value]) {
        return accumulator;
      }

      // check if the thermostat should be filtered
      const validate = (thermostat) => {
        const duplicate = accumulator.some((filteredThermostat) => thermostat.uid_hash === filteredThermostat.uid_hash);
        let isAMatch = false;

        // account for demands being structured differently
        if (key === 'demand') {
          isAMatch = thermostat[value];
        } else {
          isAMatch = thermostat[key] === value;
        }
        return !duplicate && isAMatch;
      };
      return [...accumulator, ...thermostats.filter(validate)];
    }, []);
  }

  static updateFilterCounts(thermostats) {
    return (dispatch) => {
      const defaultCounter = {
        mode: {
          auto: 0,
          auxHeatOnly: 0,
          cool: 0,
          eco: 0,
          heat: 0,
          heat_cool: 0,
          off: 0,
        },
        fan_mode: {
          auto: 0,
          off: 0,
          on: 0,
        },
        connection_state: {
          offline: 0,
          online: 0,
          reset: 0,
        },
        state: {
          aux_heat: 0,
          cool: 0,
          heat: 0,
          off: 0,
        },
        demand: {
          is_fan_running: 0,
          is_occupied: 0,
        },
      };

      const counter = thermostats.reduce((acc, thermostat) => {
        const {
          mode: modeKey,
          fan_mode: fanModeKey,
          connection_state: connectionStateKey,
          state: stateKey,
        } = thermostat;

        acc.mode[modeKey] += 1;
        acc.fan_mode[fanModeKey] += 1;
        acc.connection_state[connectionStateKey] += 1;
        acc.state[stateKey] += 1;

        if (thermostat.is_fan_running) {
          acc.demand.is_fan_running += 1;
        }

        if (thermostat.is_occupied) {
          acc.demand.is_occupied += 1;
        }

        return acc;
      }, defaultCounter);

      return dispatch({ type: ThermostatConstants.UpdateFilterCounts, payload: counter });
    };
  }

  static updateNumberOfFilters(number) {
    return { type: ThermostatConstants.UpdateNumberOfFilters, payload: number };
  }

  static clearErrors() {
    return { type: ThermostatConstants.ClearThermostatError };
  }

  static stopRegistering() {
    return { type: ThermostatConstants.StopRegistering };
  }
}
